/*
JavaScript component managing both desktop and mobile navigation functionalities.

Key features include:
- Desktop Navigation: Manages submenu visibility and interaction on desktop screens.
- Mobile Navigation: Handles opening and closing of mobile navigation menus, including second-tier submenus.
- Data Initialization: Retrieves and sets up navigation data from the DOM.
- Swiper Configuration: Sets up Swiper instances for smooth navigation on mobile devices.
- Event Handling: Manages events like mouse clicks and mouse enters to control submenu behavior.

The component is integrated with Alpine.js, utilizing its reactive data management and lifecycle hooks to handle navigation interactions seamlessly.
*/

const swiperConfig = {
  slidesPerView: 'auto',
  spaceBetween: 16,
  slidesOffsetBefore: 24,
  slidesOffsetAfter: 24,
};

const navStore = () => ({
  navData: [],
  mobileNavFeaturedData: undefined,
  mobileNavFooterData: undefined,
  isMobileNavOpen: false,
  dropdownActive: false,

  init() {
    this.setData();
  },

  setData() {
    const submenus = document.querySelectorAll('.nav-submenu-data');
    this.navData = Array.from(submenus).map((submenu) => JSON.parse(submenu.textContent));

    const mobileNavFeaturedData = document.querySelector('.mobile-nav-featured-data');
    this.mobileNavFeaturedData = mobileNavFeaturedData ? JSON.parse(mobileNavFeaturedData.textContent) : null;

    const mobileNavFooterData = document.querySelector('.mobile-nav-footer-data');
    this.mobileNavFooterData = JSON.parse(mobileNavFooterData.textContent);
  },
});

function navDesktopComponent() {
  return {
    selectedSubmenuIdx: null,

    init() {
      this.$watch('selectedSubmenuIdx', (val) => {
        if (val === null) {
          document.querySelector('header.page-header').classList.remove('submenu-open');
          this.$dispatch('close-desktop-nav');
          this.$store.nav.dropdownActive = false;
          document.documentElement.style.overflow = '';
        } else if (val >= 0) {
          document.querySelector('header.page-header').classList.add('submenu-open');
          this.$dispatch('open-desktop-nav');
          this.$store.nav.dropdownActive = true;
          document.documentElement.style.overflow = 'hidden';
        }
      });
    },

    handleTopCategoryClick(idx) {
      /**
       * If in Theme Editor, pin this submenu open
       */
      if (window.Shopify.designMode) {
        if (!this.isDesignPinned) {
          this.selectedSubmenuIdx = idx;
          this.isDesignPinned = true;
        } else if (this.isDesignPinned) {
          // Closing pin
          this.selectedSubmenuIdx = null;
          this.isDesignPinned = false;
        }
      }
    },

    handleTopCategoryMouseenter(idx) {
      if (this.isDesignPinned) {
        return;
      }
      this.selectedSubmenuIdx = idx;
    },

    handleTopCategoryMouseleave() {
      if (this.isDesignPinned) {
        return;
      }

      this.selectedSubmenuIdx = null;
    },
  };
}

const navMobileComponent = () => ({
  selectedSubmenuIdx: null,

  init() {
    const tier2Element = this.$root.querySelector('.anatta-mobile-nav--tier-2');
    const drawerDrawer = tier2Element.closest('.drawer__drawer');
    if (drawerDrawer) {
      drawerDrawer.classList.add('drawer__drawer-tier-2-nav');
    }
    this.$watch('selectedSubmenuIdx', (val) => {
      if (!Number.isNaN(val) && val !== null) {
        const secondTierDialog = this.$root.querySelector('dialog');
        secondTierDialog.showModal();
      }
    });

    this.$watch('submenuFormat', (val) => val === 'linklists-and-swiper' && this.initSecondaryFeatureSwiper());
    this.initFirstTierSwiper();

    const submenuThumbnail = this.$root.querySelectorAll('.anatta-mobile-nav__thumbnail-wrapper');
    submenuThumbnail.forEach((thumbnailWrapper) => {
      const submenuBlock = thumbnailWrapper.closest('.anatta-mobile-nav__submenu_blocks');
      if (submenuBlock) {
        submenuBlock.classList.add('anatta-nav-thumbnail');
      }
    });
  },

  get selectedSubmenuLinklists() {
    return this.$store.nav.navData[this.selectedSubmenuIdx]?.blocks.filter((block) => block.type === 'link_list');
  },

  get selectedSubmenuCardBlocks() {
    return this.$store.nav.navData[this.selectedSubmenuIdx]?.blocks.filter(
      (block) => block.type === 'image_link' || block.type === 'cta_card_link',
    );
  },

  get selectedSubmenuTitle() {
    return this.$store.nav.navData[this.selectedSubmenuIdx]?.settings.nav_top_category_title;
  },

  get mobileNavFeaturedCardBlocks() {
    return this.$store.nav.mobileNavFeaturedData?.blocks;
  },

  get mobileNavFooterBlocks() {
    return this.$store.nav.mobileNavFooterData?.blocks;
  },

  get mobileNavFooterLinks() {
    return this.$store.nav.mobileNavFooterData?.blocks[0].links;
  },

  get mobileNavStyles() {
    return this.$store.nav.mobileNavFooterData?.settings || {};
  },

  get allSubmenuBlocks() {
    return this.$store.nav.navData[this.selectedSubmenuIdx]?.blocks;
  },

  get submenuBlocksAddedKeys() {
    let blocks = this.$store.nav.navData[this.selectedSubmenuIdx]?.blocks;
    for (let i = 0; i < blocks?.length; i++) {
      const currentType = blocks[i].type;
      const prevType = i > 0 ? blocks[i - 1].type : null;
      const nextType = i < blocks?.length - 1 ? blocks[i + 1].type : null;

      if (currentType !== prevType && currentType !== nextType) {
        blocks[i].firstOrLast = 'only';
      } else if (currentType !== prevType) {
        blocks[i].firstOrLast = 'first';
      } else if (currentType !== nextType) {
        blocks[i].firstOrLast = 'last';
      } else {
        blocks[i].firstOrLast = 'none';
      }
    }
    return blocks;
  },

  get submenuFormat() {
    const submenuBlocks = this.$store.nav.navData[this.selectedSubmenuIdx]?.blocks;
    if (!submenuBlocks) {
      return undefined;
    }
    if (submenuBlocks.every((block) => block.type === 'image_link' || block.type === 'cta_card_link')) {
      return 'thumbnail-only';
    }
    if (
      submenuBlocks.some((block) => block.type === 'link_list') &&
      submenuBlocks.some((block) => block.type === 'image_link' || block.type === 'cta_card_link')
    ) {
      return 'linklists-and-swiper';
    } else {
      return 'linklists-only';
    }
  },

  openMobileNav() {
    this.$el.closest('dialog').showModal();
    this.$store.nav.isMobileNavOpen = true;
  },
  openSecondTier(idx) {
    this.selectedSubmenuIdx = idx;
  },

  closeMobileNav() {
    this.$el.closest('dialog').close();
    this.selectedSubmenuIdx = null;
    this.$store.nav.isMobileNavOpen = false;
  },

  detect2ndTierClose(e) {
    if (this.$root.contains(e.target)) {
      this.selectedSubmenuIdx = null;
    }
  },

  initFirstTierSwiper() {
    const swiperEl = this.$root.querySelector('.mobile-nav--tier-1 .swiper');

    new Swiper(swiperEl, swiperConfig);
  },

  initSecondaryFeatureSwiper() {
    const swiperEl = this.$root.querySelector('.mobile-nav--tier-2 .swiper');

    new Swiper(swiperEl, swiperConfig);
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.store('nav', navStore());
  Alpine.data('navDesktop', navDesktopComponent);
  Alpine.data('navMobile', navMobileComponent);
});
